/**
 * Floor label printing picks a station (Inventory, Receiving, Shipping).
 * The hub resolves one printer with the label's stock size and DPI.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { apiFetch } from "@/lib/api";
import {
  fetchZplPrinters,
  ZPL_PRINTERS_QUERY_KEY,
  type ZplPrinterListPayload,
  type ZplPrinterStorageKey,
} from "@/lib/zplPrinter";
import {
  isZplPrintStationId,
  resolveZplPrinterAtStation,
  stationIdFromStoredPrinter,
  type PublicStationOption,
  type ZplPrintDocument,
} from "@/lib/zplPrintLocationMatch";
import {
  readFloorDeviceId,
  readScopedZplPrinter,
  writeScopedZplPrinter,
} from "@/lib/floorPrinterStorage";

const STATIONS_CACHE_KEY = "zpl-stations-cache-v1";
const PRINTERS_CACHE_KEY = "zpl-printers-cache-v1";

type CachedQuery<T> = { at: number; data: T };

function readCachedQuery<T>(key: string): CachedQuery<T> | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) ?? "") as CachedQuery<T>;
    if (parsed == null || typeof parsed.at !== "number" || parsed.data == null) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

function writeCachedQuery<T>(key: string, data: T): void {
  if (typeof window === "undefined") return;
  try {
    const payload: CachedQuery<T> = { at: Date.now(), data };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // A full localStorage must not block printing.
  }
}

export function readSavedStationId(storageKey: string, userId: string, deviceId: string): string {
  const stored = readScopedZplPrinter(storageKey, {
    userId,
    deviceId,
    mobility: "mobile",
    fallback: "",
  });
  return isZplPrintStationId(stored) ? stored : "";
}

export function zplStationsQueryOptions(): {
  queryKey: readonly ["public-stations"];
  queryFn: () => Promise<{ stations: PublicStationOption[] }>;
  staleTime: number;
  placeholderData: (
    previous: { stations: PublicStationOption[] } | undefined,
  ) => { stations: PublicStationOption[] } | undefined;
} {
  return {
    queryKey: ["public-stations"],
    queryFn: async () => {
      const data = await apiFetch<{ stations: PublicStationOption[] }>("/api/zpl-printers/stations");
      writeCachedQuery(STATIONS_CACHE_KEY, data);
      return data;
    },
    staleTime: 120_000,
    placeholderData: (previous) =>
      previous ?? readCachedQuery<{ stations: PublicStationOption[] }>(STATIONS_CACHE_KEY)?.data,
  };
}

export function zplPrintersQueryOptions(): {
  queryKey: typeof ZPL_PRINTERS_QUERY_KEY;
  queryFn: () => Promise<ZplPrinterListPayload>;
  staleTime: number;
  placeholderData: (
    previous: ZplPrinterListPayload | undefined,
  ) => ZplPrinterListPayload | undefined;
} {
  return {
    queryKey: ZPL_PRINTERS_QUERY_KEY,
    queryFn: async () => {
      const data = await fetchZplPrinters();
      writeCachedQuery(PRINTERS_CACHE_KEY, data);
      return data;
    },
    staleTime: 120_000,
    placeholderData: (previous) => previous ?? readCachedQuery<ZplPrinterListPayload>(PRINTERS_CACHE_KEY)?.data,
  };
}

export {
  resolveZplPrinterAtStation,
  stationIdFromStoredPrinter,
  zplPrintStationLabel,
  type PublicStationOption,
  type ZplLabelRequirement,
  type ZplPrintDocument,
  type ZplPrinterResolution,
} from "@/lib/zplPrintLocationMatch";

export function useZplPrintLocation(options: {
  storageKey: ZplPrinterStorageKey | string;
  /** Documents this screen can print. The first row is the one this control sends. */
  documents: readonly ZplPrintDocument[];
  fetchPrintersEnabled?: boolean;
}): {
  printersQuery: UseQueryResult<ZplPrinterListPayload>;
  stations: PublicStationOption[];
  station: string;
  setStation: (station: string) => void;
  selectedPrinter: string;
  unavailableMessage: string | null;
  documents: readonly ZplPrintDocument[];
} {
  const { session } = useAuth();
  const userId = session?.user?.id ?? "";
  const deviceId = readFloorDeviceId();

  const printersQuery = useQuery({
    ...zplPrintersQueryOptions(),
    enabled: options.fetchPrintersEnabled !== false,
  });
  const stationsQuery = useQuery({
    ...zplStationsQueryOptions(),
    enabled: options.fetchPrintersEnabled !== false,
  });

  const [station, setStationState] = useState(() =>
    readSavedStationId(options.storageKey, userId, deviceId),
  );

  useEffect(() => {
    const stored = readScopedZplPrinter(options.storageKey, {
      userId,
      deviceId,
      mobility: "mobile",
      fallback: "",
    });
    if (isZplPrintStationId(stored)) {
      setStationState(stored);
      return;
    }
    const stations = stationsQuery.data?.stations;
    if (stations == null) return;
    const mapped = stationIdFromStoredPrinter(stored, stations, printersQuery.data?.printers);
    if (mapped !== "") setStationState(mapped);
  }, [deviceId, options.storageKey, printersQuery.data?.printers, stationsQuery.data?.stations, userId]);

  useEffect(() => {
    if (!isZplPrintStationId(station)) return;
    writeScopedZplPrinter(options.storageKey, station, { userId, deviceId });
  }, [deviceId, options.storageKey, station, userId]);

  const primary = options.documents[0];
  const resolution = useMemo(
    () =>
      resolveZplPrinterAtStation(printersQuery.data?.printers ?? [], station, {
        labelSize: primary?.labelSize ?? "",
        dpi: primary?.dpi ?? 0,
      }),
    [options.documents, printersQuery.data?.printers, station],
  );

  return {
    printersQuery,
    stations: stationsQuery.data?.stations ?? [],
    station,
    setStation: setStationState,
    selectedPrinter: resolution.printerName,
    unavailableMessage: station.trim() === "" ? null : resolution.unavailableMessage,
    documents: options.documents,
  };
}
